/* =====================================================================
 * Billing-cache adapter registry (DI bridge to host React Query).
 *
 * `tokenExhaustion` (inside @auw/stores) needs to know whether the
 * current account is in a trialing state so it can route to the
 * upgrade modal vs. a plain toast. In the B2C host that information
 * lives in a React-Query cache keyed by `queryKeys.billing.status()`.
 * To keep @auw/stores UI-agnostic (and to let B2B tenants opt out of
 * billing entirely), the host registers an adapter at bootstrap and
 * the store reads from it at call time. If no adapter is registered
 * (e.g. on-prem build without billing) the store falls back to a
 * generic toast.
 * ===================================================================== */

import type { BillingStatus } from '@auw/api-client';

export interface BillingCacheAdapter {
  /** Return the latest cached billing status, or null if none. */
  get(): BillingStatus | null;
  /**
   * Fetch a fresh billing status, populating the underlying cache as
   * a side effect. Callers await this only when `get()` returned null.
   */
  fetch(): Promise<BillingStatus>;
}

let _adapter: BillingCacheAdapter | null = null;

export function setBillingCacheAdapter(adapter: BillingCacheAdapter | null): void {
  _adapter = adapter;
}

export function getBillingCacheAdapter(): BillingCacheAdapter | null {
  return _adapter;
}
